import { Bookmark, Heart, MessageCircle, PenLine, Repeat2, Share } from 'lucide-react'

import type { Post } from '@/api/types'
import { Menu, MenuItem } from '@/components/ui/Menu'
import { useToast } from '@/components/ui/Toast'
import { useT } from '@/i18n'
import { useFormat } from '@/lib/format'
import { cn } from '@/lib/cn'
import { useUiStore } from '@/stores/ui'

import { targetIdOf, useBookmark, useLike, useRepost } from '../hooks/usePostMutations'

interface Props {
  post: Post
  /** 详情页的操作栏更大一些 */
  size?: 'feed' | 'detail'
  onRequireLogin?: () => void
}

/** 回复 / 转发 / 点赞 / 分享。点击不冒泡到卡片。 */
export function PostActions({ post, size = 'feed', onRequireLogin }: Props) {
  const like = useLike()
  const repost = useRepost()
  const bookmark = useBookmark()
  const toast = useToast()
  const openComposer = useUiStore((state) => state.openComposer)
  const t = useT()

  const viewer = post.viewer
  const targetId = targetIdOf(post)
  // 线程作者限制了谁可以回复（D65）。按钮留着但置灰，让人知道不是坏了
  const replyLocked = viewer?.can_reply === false
  // 受保护账号的帖子谁都不能转发 / 引用，作者本人除外（D75）。
  // 他受保护之前就转发过的，仍然能撤销 —— 按钮留着，菜单里只剩「撤销转发」
  const protectedPost = Boolean(post.author.is_private) && !viewer?.is_author
  const shareLocked = protectedPost && !viewer?.reposted
  const iconSize = size === 'detail' ? 'size-5' : 'size-[18px]'

  const guard = (action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    if (!viewer) {
      onRequireLogin?.()
      return
    }
    action()
  }

  const share = async (event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    const url = `${window.location.origin}/${post.author.username}/status/${post.id}`
    try {
      await navigator.clipboard.writeText(url)
      toast.show(t('post.linkCopied'))
    } catch {
      toast.error(t('post.copyFailed'))
    }
  }

  return (
    <div
      className={cn(
        'mt-3 flex items-center justify-between text-ink-muted',
        size === 'detail' ? 'max-w-none' : 'max-w-md',
      )}
    >
      <ActionButton
        // 与 Composer 的"回复"提交按钮区分开，避免同名按钮让读屏用户困惑
        label={replyLocked ? t('post.cannotReply') : t('post.reply')}
        count={post.counts.replies}
        hoverClass="group-hover:text-brand-ink group-hover:bg-brand-ink/10"
        disabled={replyLocked}
        onClick={guard(() => openComposer({ replyTo: post }))}
      >
        <MessageCircle className={iconSize} />
      </ActionButton>

      {/* 转发与引用收在同一个按钮里，和 X 一样 */}
      <Menu
        align="left"
        trigger={({ toggle }) => (
          <ActionButton
            label={
              shareLocked
                ? t('protected.cannotShare')
                : viewer?.reposted
                  ? t('post.undoRepost')
                  : t('post.repost')
            }
            count={post.counts.reposts}
            active={viewer?.reposted}
            activeClass="text-repost"
            hoverClass="group-hover:text-repost group-hover:bg-repost/10"
            disabled={shareLocked}
            onClick={guard(toggle)}
          >
            <Repeat2 className={cn(iconSize, size === 'detail' && 'size-6')} />
          </ActionButton>
        )}
      >
        {(close) => (
          <>
            <MenuItem
              onClick={() => {
                close()
                repost.mutate({ id: targetId, reposted: Boolean(viewer?.reposted) })
              }}
            >
              <Repeat2 className="size-4" />
              {viewer?.reposted ? t('post.undoRepost') : t('post.repost')}
            </MenuItem>
            {!protectedPost && (
              <MenuItem
                onClick={() => {
                  close()
                  openComposer({ quoteOf: post })
                }}
              >
                <PenLine className="size-4" /> {t('post.quote')}
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      <ActionButton
        label={viewer?.liked ? t('post.unlike') : t('post.like')}
        count={post.counts.likes}
        active={viewer?.liked}
        activeClass="text-like"
        hoverClass="group-hover:text-like group-hover:bg-like/10"
        onClick={guard(() => like.mutate({ id: targetId, liked: Boolean(viewer?.liked) }))}
      >
        <Heart className={cn(iconSize, viewer?.liked && 'fill-current')} />
      </ActionButton>

      <ActionButton
        // 书签不显示计数：它是私有的，露出数字等于把别人的收藏行为公开
        label={viewer?.bookmarked ? t('post.unbookmark') : t('post.bookmark')}
        active={viewer?.bookmarked}
        activeClass="text-brand-ink"
        hoverClass="group-hover:text-brand-ink group-hover:bg-brand-ink/10"
        onClick={guard(() =>
          bookmark.mutate({ id: targetId, bookmarked: Boolean(viewer?.bookmarked) }),
        )}
      >
        <Bookmark className={cn(iconSize, viewer?.bookmarked && 'fill-current')} />
      </ActionButton>

      <ActionButton
        label={t('post.share')}
        hoverClass="group-hover:text-brand-ink group-hover:bg-brand-ink/10"
        onClick={share}
      >
        <Share className={iconSize} />
      </ActionButton>
    </div>
  )
}

function ActionButton({
  label,
  count,
  active,
  activeClass,
  hoverClass,
  disabled = false,
  onClick,
  children,
}: {
  label: string
  count?: number
  active?: boolean
  activeClass?: string
  hoverClass?: string
  disabled?: boolean
  onClick: (event: React.MouseEvent) => void
  children: React.ReactNode
}) {
  const { compactCount } = useFormat()
  return (
    <button
      type="button"
      onClick={(event) => {
        if (disabled) {
          // 点不动也不能冒泡到卡片上跳走
          event.stopPropagation()
          event.preventDefault()
          return
        }
        onClick(event)
      }}
      aria-label={label}
      aria-pressed={active}
      aria-disabled={disabled || undefined}
      title={disabled ? label : undefined}
      className={cn(
        'group flex items-center gap-1 text-[13px]',
        active && activeClass,
        disabled && 'cursor-not-allowed opacity-40',
      )}
    >
      <span className={cn('rounded-md p-2 transition-colors', !disabled && hoverClass)}>
        {children}
      </span>
      {count !== undefined && count > 0 && (
        <span className="font-mono text-[13px] tabular-nums">{compactCount(count)}</span>
      )}
    </button>
  )
}
